import { useMemo } from "react";

import { Error } from "@/pages";
import { createLegacyPageRegistryHelpers } from "./pageRegistry/legacyPageRegistryHelpers";
import { createUseLegacyPageRouting } from "./pageRegistry/createUseLegacyPageRouting";
import type { LegacyPageConfig } from "./pageRegistry/legacyPageRegistryTypes";
import {
  useLegacyPageContext,
  useLegacyPageRegistry,
} from "./pageRegistry/useLegacyPageRegistry";
import FullPageLoading from "./components/FullPageLoading";

/**
 * LegacyDynamicPageRouter
 *
 * Substitui o antigo fluxo `usePages → CurrentPage`.
 *
 * Responsabilidades:
 * - centralizar loading de sessão
 * - centralizar guard global (termos/autorização)
 * - resolver e renderizar a página pelo param `:id` do legado
 * - fallback consistente para rota inválida (Error)
 *
 * Importante (estado local das páginas):
 * - Renderizamos a página neste próprio componente (tipo estável), em vez de
 *   recriar um `RouterComponent` via fábrica a cada re-render.
 * - Recriar o tipo do componente filho faz o React remountar a página e zerar
 *   estados locais (ex.: abas Ativos/Convidados/Inativos em ManagePractitioners)
 *   sempre que um contexto pai atualiza (ex.: refetch de PractitionerRole).
 */
export default function LegacyDynamicPageRouter() {
  const registry = useLegacyPageRegistry();
  const context = useLegacyPageContext();

  const helpers = useMemo(() => {
    return createLegacyPageRegistryHelpers(registry);
  }, [registry]);

  /**
   * Page default do legado:
   * - manter comportamento atual (renderiza a primeira página `initial` ativa
   *   quando não existe `:id`).
   * - fallback para "home".
   */
  const defaultPageName = useMemo(() => {
    const activePages = Object.values(registry).filter(
      (config) => config.meta?.isEnabled?.(context) ?? true,
    );

    const initialConfig = activePages.find(
      (config) => config.meta?.legacyFeature?.initial,
    );

    const initialPageName = initialConfig?.path?.startsWith("/")
      ? initialConfig.path.slice(1)
      : undefined;

    return initialPageName ?? "home";
  }, [context, registry]);

  const useLegacyPageRouting = useMemo(() => {
    return createUseLegacyPageRouting({
      getPageConfig: (pageName) => helpers.getPageConfig(pageName),
      isPageRegistered: (pageName) => helpers.isPageRegistered(pageName),
      defaultPageName,
      paramName: "id",
    });
  }, [defaultPageName, helpers]);

  const routing = useLegacyPageRouting();
  const { PageComponent, isValidPage, pageConfig } = routing;

  // Loading global (sessão)
  if (context.session?.loading) {
    return <FullPageLoading />;
  }

  // Not found
  if (!isValidPage || !PageComponent || !pageConfig) {
    return <Error />;
  }

  // Guard global (termos/autorização)
  const person = context.session?.person;
  const roles = context.session?.roles;

  const hasTermRefused = person?.extension?.find((item: any) =>
    item.url.includes("termRefused"),
  )?.valueBoolean;

  const isAuthorizedRole = roles?.some(
    (role: any) => role.isOwner || role.isManager || role.isPractitioner,
  );

  const canNavigate = (hasTermRefused && isAuthorizedRole) || person?.active;

  if (!canNavigate) {
    const termsConfig: LegacyPageConfig | undefined =
      helpers.getPageConfig("terms");

    const TermsComponent = termsConfig?.component;
    if (TermsComponent) {
      // Mantém comportamento do legado: renderiza Termos sem redirecionar.
      return <TermsComponent />;
    }

    return <Error />;
  }

  // Guard por página
  const pageGuard = pageConfig.meta?.guard;
  if (pageGuard) {
    const guardResult = pageGuard(context);
    if (!guardResult.allow) {
      return <>{guardResult.render}</>;
    }
  }

  // Mesmo tipo entre re-renders ⇒ React preserva o state local da página.
  return <PageComponent />;
}
